import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";
import { useAuth } from "../components/dnh/AuthContext";
import { useKnowledgeBrief } from "../components/dnh/search/useKnowledgeBrief";
import { BriefResearchView } from "../components/dnh/search/TopicBrief";
import { apiFetch } from "../utils/api";

const SOURCE_LABELS = {
  wikipedia:    "Wikipedia",
  arxiv:        "arXiv",
  pubmed:       "PubMed",
  patent_uspto: "USPTO Patent",
  patent_epo:   "Espacenet",
};

const SOURCE_COLORS = {
  wikipedia:    "bg-blue-50 text-blue-700 border-blue-200",
  arxiv:        "bg-orange-50 text-orange-700 border-orange-200",
  pubmed:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  patent_uspto: "bg-purple-50 text-purple-700 border-purple-200",
  patent_epo:   "bg-violet-50 text-violet-700 border-violet-200",
};

export default function KnowledgeBriefPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const location = useLocation();
  const { user } = useAuth();
  const auto = !!user;

  const [card, setCard] = useState(location.state?.card || null);
  const { data, error, locked, busy, prepare } = useKnowledgeBrief(id, auto);

  // Fetch card metadata if not passed via router state
  useEffect(() => {
    if (card) return;
    apiFetch(`/api/knowledge/${id}`)
      .then(r => setCard(r.data))
      .catch(() => {});
  }, [id, card]);

  const backHref = q ? `/search?q=${encodeURIComponent(q)}` : "/search";

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <SiteHeader activeNav="search" user={user} ticker={false} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Back navigation */}
        <Link
          to={backHref}
          className="text-xs text-[var(--muted)] underline underline-offset-4 hover:text-[var(--ink)]"
          data-testid="kb-brief-back-link"
        >
          ← Back to search{q ? ` for "${q}"` : ""}
        </Link>

        {/* Card metadata */}
        {card && (
          <div className="mt-6" data-testid="kb-brief-card-meta">
            {card.kb_source && (
              <span
                className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded border mb-3 ${SOURCE_COLORS[card.kb_source] || "bg-gray-50 text-gray-700 border-gray-200"}`}
                data-testid="kb-brief-source-badge"
              >
                {SOURCE_LABELS[card.kb_source] || card.source_name}
              </span>
            )}
            <h1 className="font-serif text-2xl sm:text-3xl leading-snug" data-testid="kb-brief-title">
              {card.headline}
            </h1>
            {card.article_url && (
              <a
                href={card.article_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[var(--muted)] underline mt-2 inline-block"
                data-testid="kb-brief-source-link"
              >
                View original source ↗
              </a>
            )}
          </div>
        )}

        {/* Notice bar */}
        <div className="flex items-start gap-2 mt-6 p-3 rounded bg-[var(--surface)] border border-[var(--hairline)] text-xs text-[var(--muted)]" data-testid="kb-brief-notice">
          <BookOpen size={13} className="shrink-0 mt-0.5" />
          <span>
            This is a knowledge brief from a reference database, not a news story.
            DNH has not independently verified the source content.
          </span>
        </div>

        {/* Brief content */}
        <div className="mt-8" data-testid="kb-brief-content">
          {locked && (
            <div className="text-sm text-[var(--muted)] py-8 text-center" data-testid="kb-brief-locked">
              <p>Sign in to generate a knowledge brief.</p>
              <Link to="/signin" className="underline mt-2 inline-block">Sign in</Link>
            </div>
          )}
          {!locked && error && (
            <p className="text-sm text-[var(--crimson)] py-4" data-testid="kb-brief-error">{error}</p>
          )}
          {!locked && !data && !error && (
            <p className="text-sm text-[var(--muted)] py-4 animate-pulse" data-testid="kb-brief-loading">Loading…</p>
          )}
          {data && (
            <>
              {data.status === "not_prepared" && (
                <div className="py-8 text-center space-y-3" data-testid="kb-brief-not-prepared">
                  <p className="text-sm text-[var(--muted)]">
                    A brief for this knowledge item has not been generated yet.
                  </p>
                  <button
                    data-testid="kb-brief-prepare-button"
                    onClick={prepare}
                    disabled={busy}
                    className="dnh-solid-button"
                  >
                    Generate knowledge brief
                  </button>
                </div>
              )}
              {data.status === "pending" && (
                <p className="text-sm text-[var(--muted)] py-4 animate-pulse" data-testid="kb-brief-pending">
                  Preparing brief…
                </p>
              )}
              {data.status === "failed" && (
                <div className="py-8 text-center space-y-3" data-testid="kb-brief-failed">
                  <p className="text-sm text-[var(--muted)]">Brief preparation failed.</p>
                  <button data-testid="kb-brief-retry-button" onClick={prepare} className="dnh-outline-button">Retry</button>
                </div>
              )}
              {data.status === "ready" && data.edition && (
                <BriefResearchView
                  query={card?.headline || q}
                  intent={{ specific: true, clarifications: [] }}
                  research={{ data, locked: false, busy: false, prepare, selectEdition: () => {}, error: "" }}
                  singleSource={true}
                />
              )}
              {data.notice && (
                <p className="text-xs text-[var(--muted)] mt-6 italic" data-testid="kb-brief-disclaimer">
                  {data.notice}
                </p>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
